import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { useEffect, useId, useState } from 'react';
import styled from 'styled-components';

import type { ModelInfo } from '@paradigm/terminal-core';

import {
  BASE_PICKER_SECTIONS,
  resolveBaseVersions,
  resolveFeaturedVersions,
  type BasePickerEntry,
} from '../../models/base-picker';
import {
  defaultModelLabel,
  pickDefaultModelId,
  resolveModelInCatalog,
} from '../../models/model-labels';
import { privacyFromTags, type PrivacyMeta } from '../../models/privacy';
import {
  formatVersionLabel,
  groupByModelAndVersion,
  groupModelsByCompany,
  type ModelVersionEntry,
  type ModelVersionGroup,
} from '../../models/provider-groups';
import { CheckIcon, ChevronRightIcon } from '../icons';
import { MENU_ROW, MENU_SURFACE } from '../../styles/menu';
import { restoreComposerFocus } from '../../focus';
import {
  MENU_ZOOM_VAR,
  menuLayer,
  syncMenuLayer,
} from '../recents/menu-position';
import PrivacyBadge from './PrivacyBadge';

interface Props {
  readonly models: readonly ModelInfo[];
  readonly selectedId: string | null;
  readonly onSelect: (id: string) => void;
}

/**
 * How long the loading label stays invisible. The catalog usually lands well
 * inside this (the sidecar caches it for 30s), and text that appears only to be
 * replaced a moment later reads as a glitch — so the common path is a silent
 * reserved slot, and only a genuinely slow gateway gets an explanation.
 */
const LOADING_LABEL_DELAY_MS = 500;

/**
 * Stand-in tier for an id the live catalog doesn't carry — a retired gateway
 * default, or a pick persisted from an older catalog. The lookup failed, so
 * the honest report is "unknown": rendering nothing would be pixel-identical
 * to a model the catalog says has no Privacy tag at all, on a surface whose
 * whole point is that the tier in force reads as part of the model name.
 */
const UNKNOWN_PRIVACY: PrivacyMeta = {
  // Untiered, so it takes the badge's neutral colours rather than claiming
  // one of the three real tiers.
  tier: null,
  label: 'Unknown',
  definition:
    'This model is not in the current catalog, so its privacy tier could not be looked up.',
};

/**
 * Non-interactive stand-in that holds the picker's slot while the gateway's
 * catalog is in flight, or reports that it never arrived. Models come only
 * from `/api/models` (there is no built-in fallback list), so without this the
 * composer footer would collapse and then pop back in on every connect.
 *
 * The box is ALWAYS rendered at the real trigger's dimensions and hidden with
 * `visibility` rather than unmounted — that reserves the exact space the picker
 * will occupy, by construction rather than by a duplicated magic height, so
 * neither the delayed label nor the eventual swap moves anything on screen.
 */
export function ModelPickerPlaceholder({
  state,
}: {
  readonly state: 'loading' | 'unavailable';
}) {
  const [labelVisible, setLabelVisible] = useState(false);

  useEffect(() => {
    // `unavailable` is only reached after the retry budget is exhausted, so the
    // wait has already been long — show it immediately.
    if (state === 'unavailable') {
      setLabelVisible(true);
      return undefined;
    }
    const timer = setTimeout(
      () => setLabelVisible(true),
      LOADING_LABEL_DELAY_MS,
    );
    return () => clearTimeout(timer);
  }, [state]);

  const label = state === 'loading' ? 'Loading models…' : 'Models unavailable';
  return (
    // `visibility: hidden` also removes the node from the accessibility tree,
    // so the live region announces on reveal rather than at mount.
    <PlaceholderTrigger
      aria-live="polite"
      $visible={labelVisible}
      $muted={state === 'unavailable'}
    >
      {label}
    </PlaceholderTrigger>
  );
}

export default function ModelPicker({ models, selectedId, onSelect }: Props) {
  const menuId = useId();
  const [showAll, setShowAll] = useState(false);
  if (models.length === 0) return null;

  // What to NAME in the trigger. An id the catalog doesn't carry (a retired
  // gateway model, or a catalog that arrived in a different id shape) is
  // labelled from the id itself: falling back to models[0] would name the
  // session with a model it is not running, and since the catalog is sorted
  // by raw id, position 0 is arbitrary. With nothing selected yet, use the
  // same Opus-first default the provider's catalog effect applies.
  const matched = resolveModelInCatalog(models, selectedId);
  const displayId = matched?.id ?? selectedId ?? pickDefaultModelId(models);
  if (displayId == null) return null;
  // Read tags off whatever entry the trigger is NAMING, not off the selection
  // alone: with nothing picked yet the label is the catalog's Opus default,
  // whose tier the catalog does carry — `matched` is null there only because
  // there was no id to match.
  const displayed = matched ?? resolveModelInCatalog(models, displayId);
  const displayLabel = displayed?.label ?? defaultModelLabel(displayId);

  // Three outcomes, deliberately kept distinct: a tier the catalog names, a
  // catalog entry with no Privacy tag (no badge — the rule the menu rows
  // follow), and an id the catalog doesn't carry, where the tier isn't absent
  // but unknown.
  const privacy =
    displayed == null ? UNKNOWN_PRIVACY : privacyFromTags(displayed.tags);

  return (
    <DropdownMenuPrimitive.Root
      onOpenChange={(open) => {
        // The factor can have changed since this rendered — the host writes
        // `zoom` onto an element, which React never sees.
        if (open) syncMenuLayer();
        if (!open) setShowAll(false);
      }}
    >
      <Trigger
        aria-label={
          privacy == null
            ? `Model: ${displayLabel}`
            : `Model: ${displayLabel}, privacy: ${privacy.label}`
        }
      >
        <TriggerLabel>{displayLabel}</TriggerLabel>
        {privacy != null && <PrivacyBadge meta={privacy} variant="footer" />}
      </Trigger>
      <DropdownMenuPrimitive.Portal container={menuLayer()}>
        <Content
          sideOffset={6}
          align="end"
          side="top"
          onCloseAutoFocus={(e) => {
            // Prevent Radix from returning focus to the trigger button —
            // redirect it to the chat textarea so the user can type
            // immediately after picking a model.
            e.preventDefault();
            // Shared with the context meter beside it — see
            // `restoreComposerFocus`, which also carries the `aModalIsOpen`
            // bail this used to spell out inline.
            restoreComposerFocus();
          }}
        >
          {showAll ? (
            <FullList
              models={models}
              menuId={menuId}
              selectedId={displayId}
              onSelect={onSelect}
              onBack={() => setShowAll(false)}
            />
          ) : (
            <BaseList
              models={models}
              selectedId={displayId}
              onSelect={onSelect}
              onMore={() => setShowAll(true)}
            />
          )}
        </Content>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  );
}

/**
 * Default list + a "More Models →" affordance to open the full catalog. The
 * default list is the gateway's `Featured: True` models (data-driven, catalog
 * owner controls it via LiteLLM tags); when nothing is tagged featured it falls
 * back to the hand-curated BASE_PICKER_SECTIONS.
 */
function BaseList({
  models,
  selectedId,
  onSelect,
  onMore,
}: {
  readonly models: readonly ModelInfo[];
  readonly selectedId: string;
  readonly onSelect: (id: string) => void;
  readonly onMore: () => void;
}) {
  const featured = resolveFeaturedVersions(models);

  return (
    <>
      {featured.length > 0 ? (
        <Group aria-labelledby="base-heading">
          <Heading id="base-heading">Models</Heading>
          {featured.map((v) => (
            <Item key={v.id} onSelect={() => onSelect(v.id)}>
              <ItemLabel>
                {formatVersionLabel(v.parsed)}
                <RowPrivacyBadge tags={v.tags} />
              </ItemLabel>
              <CheckSlot>{v.id === selectedId && <CheckIcon />}</CheckSlot>
            </Item>
          ))}
        </Group>
      ) : (
        <CuratedSections
          models={models}
          selectedId={selectedId}
          onSelect={onSelect}
        />
      )}
      <Group>
        <Item
          onSelect={(e) => {
            e.preventDefault();
            onMore();
          }}
        >
          <ItemLabel>More Models</ItemLabel>
          <CaretSlot>
            <ChevronRightIcon size={10} />
          </CaretSlot>
        </Item>
      </Group>
    </>
  );
}

/** Hand-curated BASE_PICKER_SECTIONS — the fallback when no model is featured. */
function CuratedSections({
  models,
  selectedId,
  onSelect,
}: {
  readonly models: readonly ModelInfo[];
  readonly selectedId: string;
  readonly onSelect: (id: string) => void;
}) {
  const sections = BASE_PICKER_SECTIONS.map((section) =>
    section.filter((e) => resolveBaseVersions(models, e).length > 0),
  ).filter((section) => section.length > 0);

  return (
    <>
      {sections.map((section, si) => (
        <Group
          key={section.map((e) => e.label).join(',')}
          aria-labelledby={si === 0 ? 'base-heading' : undefined}
        >
          {si === 0 && <Heading id="base-heading">Models</Heading>}
          {section.map((entry) => (
            <BaseEntry
              key={entry.label}
              entry={entry}
              models={models}
              selectedId={selectedId}
              onSelect={onSelect}
            />
          ))}
        </Group>
      ))}
    </>
  );
}

/** One curated entry — flat when it resolves to a single model, else a flyout. */
function BaseEntry({
  entry,
  models,
  selectedId,
  onSelect,
}: {
  readonly entry: BasePickerEntry;
  readonly models: readonly ModelInfo[];
  readonly selectedId: string;
  readonly onSelect: (id: string) => void;
}) {
  const versions = resolveBaseVersions(models, entry);
  if (versions.length === 0) return null;

  if (versions.length === 1) {
    const v = versions[0]!;
    return (
      <Item onSelect={() => onSelect(v.id)}>
        <ItemLabel>
          {entry.label}
          <RowPrivacyBadge tags={v.tags} />
        </ItemLabel>
        <CheckSlot>{v.id === selectedId && <CheckIcon />}</CheckSlot>
      </Item>
    );
  }

  const multipleProviders =
    new Set(versions.map((v) => v.parsed.provider)).size > 1;
  return (
    <VersionFlyout
      label={entry.label}
      versions={versions}
      multipleProviders={multipleProviders}
      selectedId={selectedId}
      onSelect={onSelect}
    />
  );
}

/** Full catalog, grouped by company — the "More Models" view. */
function FullList({
  models,
  menuId,
  selectedId,
  onSelect,
  onBack,
}: {
  readonly models: readonly ModelInfo[];
  readonly menuId: string;
  readonly selectedId: string;
  readonly onSelect: (id: string) => void;
  readonly onBack: () => void;
}) {
  const groups = groupModelsByCompany(models);
  return (
    <>
      <Group>
        <BackItem
          onSelect={(e) => {
            e.preventDefault();
            onBack();
          }}
        >
          ‹ Models
        </BackItem>
      </Group>
      {groups.map((group, gi) => {
        const headingId = `${menuId}-group-${gi}`;
        const versionGroups = groupByModelAndVersion(group.models);
        return (
          <Group key={group.label} aria-labelledby={headingId}>
            <Heading id={headingId}>{group.label}</Heading>
            {versionGroups.map((vg) => (
              <ModelEntry
                key={vg.modelName}
                vg={vg}
                selectedId={selectedId}
                onSelect={onSelect}
              />
            ))}
          </Group>
        );
      })}
    </>
  );
}

/** A full-catalog model entry — flat item or a version flyout. */
function ModelEntry({
  vg,
  selectedId,
  onSelect,
}: {
  readonly vg: ModelVersionGroup;
  readonly selectedId: string;
  readonly onSelect: (id: string) => void;
}) {
  const sorted = vg.versions;
  if (sorted.length === 1) {
    const v = sorted[0]!;
    return (
      <Item onSelect={() => onSelect(v.id)}>
        <ItemLabel>
          {formatVersionLabel(v.parsed)}
          {v.legacy === true && <LegacyTag>Legacy</LegacyTag>}
          <RowPrivacyBadge tags={v.tags} />
        </ItemLabel>
        <CheckSlot>{v.id === selectedId && <CheckIcon />}</CheckSlot>
      </Item>
    );
  }
  return (
    <VersionFlyout
      label={vg.modelName}
      versions={sorted}
      multipleProviders={vg.hasMultipleProviders}
      selectedId={selectedId}
      onSelect={onSelect}
    />
  );
}

/**
 * A model with multiple versions/providers: a Radix Sub whose trigger carries
 * NO privacy badge — the tier can differ per provider, so it's shown only on
 * the expanded entries.
 */
function VersionFlyout({
  label,
  versions,
  multipleProviders,
  selectedId,
  onSelect,
}: {
  readonly label: string;
  readonly versions: readonly ModelVersionEntry[];
  readonly multipleProviders: boolean;
  readonly selectedId: string;
  readonly onSelect: (id: string) => void;
}) {
  const anySelected = versions.some((v) => v.id === selectedId);
  return (
    <DropdownMenuPrimitive.Sub>
      <SubTrigger>
        <ItemLabel>
          {label}
          {anySelected && <SelectedDot />}
        </ItemLabel>
        <CaretSlot>
          <ChevronRightIcon size={10} />
        </CaretSlot>
      </SubTrigger>
      <DropdownMenuPrimitive.Portal container={menuLayer()}>
        <SubContent sideOffset={4}>
          {versions.map((v) => (
            <Item key={v.id} onSelect={() => onSelect(v.id)}>
              <ItemLabel>
                {formatVersionLabel(v.parsed)}
                {v.legacy === true && <LegacyTag>Legacy</LegacyTag>}
                {multipleProviders && (
                  <ProviderTag>{v.parsed.provider}</ProviderTag>
                )}
                <RowPrivacyBadge tags={v.tags} />
              </ItemLabel>
              <CheckSlot>{v.id === selectedId && <CheckIcon />}</CheckSlot>
            </Item>
          ))}
        </SubContent>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Sub>
  );
}

/** A menu row's badge, or nothing when the catalog carries no Privacy tag. */
function RowPrivacyBadge({ tags }: { readonly tags?: readonly string[] }) {
  const meta = privacyFromTags(tags);
  if (meta == null) return null;
  return <PrivacyBadge meta={meta} />;
}

// ── Styled Components ──────────────────────────────────────────────────

const Trigger = styled(DropdownMenuPrimitive.Trigger)`
  all: unset;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 400;
  color: var(--text-label);
  cursor: pointer;

  &:hover,
  &[data-state='open'] {
    color: var(--text-primary);
    background-color: var(--hover-bg);
  }
`;

const TriggerLabel = styled.span`
  white-space: nowrap;
`;

// Mirrors `Trigger`'s box so swapping in the real picker causes no reflow —
// minus the interactive affordances (no pointer cursor, no hover state).
// `visibility` (not `display`/unmount) keeps the box in layout while hidden,
// which is what reserves the row's height before the label is revealed.
const PlaceholderTrigger = styled.span<{
  readonly $visible: boolean;
  readonly $muted: boolean;
}>`
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 400;
  white-space: nowrap;
  color: var(--text-label);
  cursor: default;
  user-select: none;
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  opacity: ${({ $muted }) => ($muted ? 0.6 : 1)};
`;

const Content = styled(DropdownMenuPrimitive.Content)`
  /* Scaled back up by the layer that cancelled the ambient zoom so Radix's
     transform could land where it says — see "menuLayer". */
  zoom: var(${MENU_ZOOM_VAR}, 1);
  ${MENU_SURFACE}
  min-width: 220px;
  /* Screen-pixel measurement inside a zoomed panel — see SettingsSelect. */
  max-height: calc(
    var(--radix-dropdown-menu-content-available-height) /
      var(${MENU_ZOOM_VAR}, 1)
  );
  z-index: 2000;
`;

const SubContent = styled(DropdownMenuPrimitive.SubContent)`
  /* Portalled into the same zoom-cancelled layer as Content, so it needs the
     same scale-back or the submenu renders unscaled at any UI scale != 100%. */
  zoom: var(${MENU_ZOOM_VAR}, 1);
  ${MENU_SURFACE}
  min-width: 200px;
  /* Screen-pixel measurement inside a zoomed panel — see SettingsSelect. */
  max-height: calc(
    var(--radix-dropdown-menu-content-available-height) /
      var(${MENU_ZOOM_VAR}, 1)
  );
  z-index: 2000;
`;

const Group = styled(DropdownMenuPrimitive.Group)`
  & + & {
    margin-top: 6px;
    padding-top: 6px;
    border-top: 1px solid var(--border-color);
  }
`;

const Heading = styled(DropdownMenuPrimitive.Label)`
  padding: 6px 8px 4px;
  font-size: 0.7rem;
  color: var(--text-label);
`;

const Item = styled(DropdownMenuPrimitive.Item)`
  ${MENU_ROW}
  /* A fixed trailing column so the checks line up down the list rather than
     sitting ragged against labels of different lengths. The extra left inset
     indents entries under their group heading. */
  display: grid;
  grid-template-columns: minmax(0, 1fr) 16px;
  padding-left: 16px;
`;

const BackItem = styled(DropdownMenuPrimitive.Item)`
  ${MENU_ROW}
  /* Muted until touched: it is a way out, not one of the choices. */
  color: var(--text-label);

  &[data-highlighted],
  &:hover {
    color: var(--text-primary);
  }
`;

const SubTrigger = styled(DropdownMenuPrimitive.SubTrigger)`
  ${MENU_ROW}
  display: grid;
  grid-template-columns: minmax(0, 1fr) 16px;
  padding-left: 16px;

  /* An open submenu keeps its parent row lit, so the trail back is visible. */
  &[data-state='open'] {
    background: var(--hover-bg);
  }
`;

const ItemLabel = styled.span`
  display: flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LegacyTag = styled.span`
  font-size: 0.7rem;
  color: var(--text-label);
`;

const ProviderTag = styled.span`
  font-size: 0.65rem;
  padding: 0 4px;
  border-radius: 3px;
  background: var(--hover-bg);
  color: var(--text-label);
`;

const CheckSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--text-primary);
`;

const CaretSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--text-label);
`;

const SelectedDot = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--text-primary);
  flex-shrink: 0;
`;
